import { BarChart3, ExternalLink, Pencil, Plus } from "lucide-react";
import { useLocaleState, useNotify, useTranslate, useUpdate } from "ra-core";
import type { ReactNode } from "react";

import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/atomic-crm/misc/ResponsiveDialog";
import { SELECT_CLASS } from "@/components/market-intel/FormDialog";
import { formatShortDate } from "@/components/market-intel/dates";
import { Button } from "@/components/ui/button";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

import { pieceDate } from "./calendar";
import {
  CONTENT_STATUSES,
  statusLabelKey,
  verticalLabelKey,
} from "./constants";
import { useMarketing } from "./context";
import { sumMetrics } from "./analytics";
import { findById, saleName } from "./hooks";
import { isOverdue, nextIndex } from "./pipeline";
import type { ContentItem, ContentStatus } from "./types";
import {
  ChannelLabel,
  FormatChip,
  PillarChips,
  SaleAvatar,
  StatusBadge,
} from "./ui";

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex min-w-0 items-start justify-between gap-3 border-b border-border/60 py-2.5 text-sm last:border-b-0">
    <dt className="shrink-0 text-muted-foreground">{label}</dt>
    <dd className="min-w-0 text-right font-medium [overflow-wrap:anywhere]">
      {children}
    </dd>
  </div>
);

const METRIC_COLUMNS = [
  "reach",
  "saves",
  "shares",
  "direct_messages",
  "leads",
] as const;

/** A piece opened from anywhere (?pieza=ID): details + weekly metrics. */
export const PieceSheet = ({
  piece,
  open,
  onClose,
}: {
  piece: ContentItem | undefined;
  open: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const notify = useNotify();
  const { data, sales, editPiece, recordMetrics } = useMarketing();
  const [update] = useUpdate();
  const title = piece?.title ?? translate("marketing.piece.title");

  const changeStatus = (status: ContentStatus) => {
    if (!piece || status === piece.status) return;
    update(
      "content_items",
      {
        id: piece.id,
        data: {
          status,
          index: nextIndex(data.items, status),
          published_at:
            status === "publicado"
              ? (piece.published_at ?? new Date().toISOString())
              : null,
        },
        previousData: piece,
      },
      {
        mutationMode: "optimistic",
        onSuccess: () =>
          notify("marketing.pipeline.moved", {
            type: "success",
            messageArgs: { status: translate(statusLabelKey(status)) },
          }),
        onError: () => notify("marketing.pipeline.error", { type: "error" }),
      },
    );
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      history={false}
    >
      <ResponsiveDialogContent
        mobileTitle={translate("marketing.piece.title")}
        className="md:max-h-[90dvh] md:overflow-y-auto sm:max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="text-lg leading-snug font-semibold [overflow-wrap:anywhere] max-md:text-xl">
            {title}
          </DialogTitle>
        </DialogHeader>
        {!piece ? (
          <p className="text-sm text-muted-foreground">
            {translate("marketing.piece.not_found")}
          </p>
        ) : (
          renderBody({
            piece,
            locale,
            onStatus: changeStatus,
            onEdit: () => editPiece(piece),
            onRecord: (row) =>
              recordMetrics({ kind: "piece", id: piece.id }, row),
          })
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );

  function renderBody({
    piece,
    locale,
    onStatus,
    onEdit,
    onRecord,
  }: {
    piece: ContentItem;
    locale: string;
    onStatus: (status: ContentStatus) => void;
    onEdit: () => void;
    onRecord: (row?: (typeof data.metrics)[number]) => void;
  }) {
    const channel = findById(data.channels, piece.channel_id);
    const plan = findById(data.plans, piece.plan_id);
    const phase = findById(data.phases, piece.phase_id);
    const assignee = findById(sales, piece.assignee_id);
    const siblings = data.pillars.filter(
      (pillar) => String(pillar.plan_id) === String(piece.plan_id),
    );
    const pillars = [piece.pillar_id, piece.secondary_pillar_id]
      .map((id) => findById(data.pillars, id))
      .filter((pillar) => pillar != null);
    const date = pieceDate(piece);
    const rows = data.metrics
      .filter((row) => String(row.content_item_id) === String(piece.id))
      .sort((a, b) => b.week_start.localeCompare(a.week_start));
    const totals = sumMetrics(rows);
    const overdue = isOverdue(piece, new Date());

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={piece.status} />
          <FormatChip format={piece.format} />
          <PillarChips pillars={pillars} siblings={siblings} withName />
          {overdue ? (
            <span className="mk-overdue">
              {translate("marketing.common.overdue")}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={onEdit}
          >
            <Pencil aria-hidden className="size-4" />
            {translate("marketing.piece.edit")}
          </Button>
          <Button
            type="button"
            className="btn-save rounded-full"
            onClick={() => onRecord()}
          >
            <BarChart3 aria-hidden className="size-4" />
            {translate("marketing.piece.record")}
          </Button>
          {piece.published_url ? (
            <Button
              variant="ghost"
              className="rounded-full"
              nativeButton={false}
              render={
                <a
                  href={piece.published_url}
                  target="_blank"
                  rel="noreferrer noopener"
                />
              }
            >
              <ExternalLink aria-hidden className="size-4" />
              {translate("marketing.piece.view_post")}
            </Button>
          ) : null}
        </div>
        <dl className="mi-card px-4 py-1">
          <Row label={translate("marketing.status.label")}>
            <label className="sr-only" htmlFor={`mk-status-${piece.id}`}>
              {translate("marketing.status.label")}
            </label>
            <select
              id={`mk-status-${piece.id}`}
              className={cn(SELECT_CLASS, "h-8 w-auto py-0 text-sm md:h-8")}
              value={piece.status}
              onChange={(event) =>
                onStatus(event.target.value as ContentStatus)
              }
            >
              {CONTENT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {translate(statusLabelKey(status))}
                </option>
              ))}
            </select>
          </Row>
          <Row label={translate("marketing.piece.channel")}>
            <ChannelLabel channel={channel} className="justify-end" />
          </Row>
          <Row
            label={translate(
              piece.status === "publicado"
                ? "marketing.piece.published"
                : "marketing.piece.scheduled",
            )}
          >
            {date ? (
              <span className="inline-flex items-center gap-1.5 first-letter:uppercase">
                {formatDate(date, locale, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            ) : (
              translate("marketing.piece.no_date")
            )}
          </Row>
          {plan ? (
            <Row label={translate("marketing.piece.plan")}>
              {plan.name}
              {piece.week_number || phase ? (
                <span className="block text-xs font-normal text-muted-foreground">
                  {[
                    phase
                      ? phase.name
                        ? `${translate("marketing.plans.phase_name", { number: phase.number })} · ${phase.name}`
                        : translate("marketing.plans.phase_name", {
                            number: phase.number,
                          })
                      : null,
                    piece.week_number
                      ? translate("marketing.common.week_long", {
                          week: piece.week_number,
                        })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              ) : null}
            </Row>
          ) : null}
          <Row label={translate("marketing.vertical.label")}>
            {translate(verticalLabelKey(piece.vertical))}
          </Row>
          <Row label={translate("marketing.piece.assignee")}>
            {assignee ? (
              <span className="inline-flex items-center gap-2">
                <SaleAvatar sale={assignee} />
                {saleName(assignee)}
              </span>
            ) : (
              translate("marketing.common.unassigned")
            )}
          </Row>
        </dl>
        {piece.brief ? (
          <section>
            <h3 className="mb-1.5 text-sm font-semibold">
              {translate("marketing.piece.brief")}
            </h3>
            <p className="text-sm whitespace-pre-line text-muted-foreground [overflow-wrap:anywhere]">
              {piece.brief}
            </p>
          </section>
        ) : null}
        <section aria-labelledby={`mk-metrics-${piece.id}`}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 id={`mk-metrics-${piece.id}`} className="text-sm font-semibold">
              {translate("marketing.piece.metrics")}
            </h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 rounded-full text-primary"
              onClick={() => onRecord()}
            >
              <Plus aria-hidden className="size-4" />
              {translate("marketing.piece.record")}
            </Button>
          </div>
          {rows.length ? (
            <div className="mi-card overflow-x-auto p-0">
              <table className="w-full min-w-[22rem] text-sm tabular-nums">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2 font-medium">
                      {translate("marketing.metrics_form.week")}
                    </th>
                    {METRIC_COLUMNS.map((field) => (
                      <th
                        key={field}
                        className="px-2 py-2 text-right font-medium"
                      >
                        {translate(`marketing.metric.${field}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer border-t border-border/60 hover:bg-muted/50"
                      onClick={() => onRecord(row)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button
                          type="button"
                          className="text-left underline-offset-2 hover:underline"
                          onClick={(event) => {
                            event.stopPropagation();
                            onRecord(row);
                          }}
                        >
                          {formatShortDate(row.week_start, locale)}
                        </button>
                      </td>
                      {METRIC_COLUMNS.map((field) => (
                        <td key={field} className="px-2 py-2 text-right">
                          {row[field] ?? "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="border-t border-border font-semibold">
                    <td className="px-3 py-2">
                      {translate("marketing.piece.metrics_total")}
                    </td>
                    {METRIC_COLUMNS.map((field) => (
                      <td key={field} className="px-2 py-2 text-right">
                        {totals[field]}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {translate("marketing.piece.metrics_empty")}
            </p>
          )}
        </section>
      </div>
    );
  }
};
